import styled, { css } from 'styled-components';
import chroma from 'chroma-js';
import { theme } from '@hox/ui/styles/theme';

interface IPropsWithTheme {
  theme: typeof theme;
}

interface IPropsWithColor {
  dim?: boolean;
  color?: 'primary' | 'inverse' | 'link';
}

interface IPropsWithBright {
  bright?: boolean;
}

type ITypographyProps = IPropsWithTheme & IPropsWithColor;

const getColor = ({
  theme,
  color = 'primary',
  dim = false,
}: ITypographyProps) => {
  let solvedColor =
    color === 'link'
      ? theme.palette.foreground.cta
      : theme.palette.foreground[color];

  if (dim) {
    solvedColor = chroma(solvedColor)
      .alpha(0.7)
      .css();
  }

  return css`
    color: ${solvedColor};
  `;
};

const headingBaseStyle = css`
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  line-height: 1.6;
  letter-spacing: 0.01875rem;
  ${getColor}
`;

export const MegaHeading = styled.div`
  ${headingBaseStyle}
  font-size: 3rem;
  line-height: 1.15;
`;

export const Heading1 = styled.div`
  ${headingBaseStyle}
  font-size: 2rem;
  line-height: 1.2;
`;

export const Heading2 = styled.div`
  ${headingBaseStyle}
  font-size: 1.375rem;
  letter-spacing: 0.02125rem;
  line-height: 1.4;
`;

// Eslint doesn't understand multiline generics
// eslint-disable-next-line no-unexpected-multiline
export const Heading3 = styled.div<IPropsWithColor>`
  ${headingBaseStyle}
  font-size: 1rem;
  letter-spacing: 0.03rem;
`;

const bodyBaseStyle = css`
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  line-height: 1.6;
  letter-spacing: 0.03rem;
  overflow: hidden;
  text-overflow: ellipsis;
  ${getColor}
`;

export const Label = styled.div`
  text-transform: uppercase;
  font-size: 0.75rem;
  opacity: 0.5;
  letter-spacing: 0.01875rem;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  line-height: 0.9375rem;
  color: ${({ theme }) => theme.palette.foreground.primary};
`;

export const Paragraph = styled.div`
  ${bodyBaseStyle};
  font-size: 1rem;
  ${(props: ITypographyProps) => getColor({ dim: true, ...props })};

  > a {
    color: inherit;
  }
`;

export const SmallText = styled.div<IPropsWithBright & ITypographyProps>`
  ${bodyBaseStyle}
  font-size: 0.75rem;
  line-height: 1.8;
  > a {
    color: inherit;
  }
  ${props => getColor({ ...props, dim: true })};
  ${({ bright, ...props }) =>
    bright &&
    css`
      font-weight: 600;
      ${getColor(props)};
    `}
`;

export const SmallTextBright = styled(SmallText).attrs({ bright: true })``;

export const InputText = Paragraph;
